import React, {useState} from 'react';
import axios from 'axios';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import {Icon} from '@/components/icons/Icon';
import {ModalAlert, useAlert} from '@/components/alerts';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useData from '@/utils/useData';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import {buildURL} from '@/utils/urlBuilder';
import TableComponent from '@/components/table/Table';
import ChannelEvaluationsTasksModal from './ChannelEvaluationsTasksModal';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {useStoreActions} from 'store';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface ChannelEvaluationTask {
    id: number;
    updatedOn: string;
    lastUpdated: string;
    createdOn: string;
    evaluationTaskTypeLabel: string;
    name: string;
    description: string;
}

interface UserData {
    permissions?: string[];
}

interface ChannelEvaluationTasksListViewProps {
    activeChannelId?: number | null;
}

const ChannelEvaluationTasksListView: React.FC<ChannelEvaluationTasksListViewProps> = React.memo((
    {
        activeChannelId = null,
    },
) => {
    const channelId = activeChannelId;

    // All hooks must be called before any conditional logic
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedEvaluationTaskId, setEvaluationTaskId] = useState<number>(0);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<{id: number; name: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    // Call useData hook unconditionally, but pass null URL when channelId is not available
    const [channelEvaluationsTasks, loading] = useData<ChannelEvaluationTask[]>(
        channelId ? buildURL('channelEvaluationsTasksResource', {obj_id: channelId}) : null,
        {
            storeResolver: 'channelEvaluationsTasks',
            loadingResolver: 'loading',
            setLoadingResolver: 'setLoading',
        }
    );

    // All hooks must be declared before any conditional returns
    const deleteEvaluationTask = useStoreActions((actions: any) => actions.channelEvaluationsTasks.deleteOne);
    const {showSuccess, showError} = useAlert();

    // Early return if userData is not loaded yet
    if (loadingUserData || !userData) {
        return <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>;
    }

    // Early return if no channel is selected
    if (!channelId) {
        return <div>{i18n.t('jt_no_channel_selected.text')}</div>;
    }

    const userCanAccessChannelsEvaluationsTasksResource = hasPermission('fs.channel.evaluation_task.list', userData?.permissions, true);
    const userCanCreateChannelsEvaluationsTasksResource = hasPermission('fs.channel.evaluation_task.create', userData?.permissions, true);
    const userCanChangeChannelsEvaluationsTasksResource = hasPermission('fs.channel.evaluation_task.update', userData?.permissions, true);
    const userCanDeleteChannelsEvaluationsTasksResource = hasPermission('fs.channel.evaluation_task.delete', userData?.permissions, true);

    if (!userCanAccessChannelsEvaluationsTasksResource) {
        return (
            <>
                <Row className="mb-3">
                    <Col lg={12} className="d-flex justify-content-end">
                        {userCanCreateChannelsEvaluationsTasksResource ? (
                            <Tooltip content={i18n.t('jt_add.text')} placement="top">
                                <Button
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-plus"
                                    onClick={() => setSettingsModalOpen(true)}>
                                    {i18n.t('jt_add_new_evaluation_task.text')}
                                </Button>
                            </Tooltip>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'flex'}}>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        icon="mdi-plus"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="channel-evaluations-tasks-list-plus-outlinesecondary-btn-1"
                                    >
                                        {i18n.t('jt_add_new_evaluation_task.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )}
                    </Col>
                </Row>

                <HelpTextBlock helpText={i18n.t('ht_you_can.text')} />

                <div className="alert alert-danger" role="alert"> {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const openSettingsModal = (evaluationTaskId: number): void => {
        setEvaluationTaskId(evaluationTaskId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setEvaluationTaskId(0);
    };

    const openDeleteConfirm = (id: number, name: string): void => {
        setItemToDelete({id, name});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setItemToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!itemToDelete || !channelId) {return;}

        setIsDeleting(true);
        try {
            await axios.delete(
                buildURL('channelEvaluationTaskDetails', {
                    channel_id: channelId,
                    obj_id: itemToDelete.id,
                }),
            );
            deleteEvaluationTask(itemToDelete.id);
            showSuccess(
                i18n.t(
                    'jt_channel_evaluation_task_deleted_success.text',
                    'Evaluation task successfully deleted'
                )
            );
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            showError(
                i18n.t(
                    'jt_channel_evaluation_task_deleted_error.text',
                    'Failed to delete evaluation task'
                )
            );
            throw error;
        }
    };

    const columns = [
        {
            accessorKey: 'updatedOn',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: ChannelEvaluationTask) => {
                if (!configLoaded) {return '';}

                if (!cell.lastUpdated || cell.lastUpdated === '-') {
                    return formatDateTime(cell.createdOn, true, {setZone: false});
                }

                return formatDateTime(cell.lastUpdated, true, {setZone: false});
            },
        },
        {
            accessorKey: 'evaluationTaskTypeLabel',
            header: i18n.t('th_evaluation_task.text'),
        },
        {
            accessorKey: 'name',
            header: i18n.t('th_name.text'),
        },
        {
            accessorKey: 'description',
            header: i18n.t('th_description.text'),
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: {row: {original: ChannelEvaluationTask}}) => {
                return (
                    <div className="d-flex align-items-center justify-content-end w-100">
                        <div className="d-inline-block">
                            <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                                <Dropdown className="d-inline-block">
                                    <Dropdown.Toggle
                                        as={Button}
                                        variant="outline-info"
                                        size="sm"
                                        icon="mdi-dots-horizontal"
                                        className="dropdown-toggle-no-caret"
                                        id="settings-dropdown-1"
                                        data-cy="channel-evaluations-tasks-list-outlineinfo-btn"
                                    />

                                    <Dropdown.Menu align="end">
                                        {
                                            userCanChangeChannelsEvaluationsTasksResource ? (
                                                <Dropdown.Item
                                                    onClick={() => openSettingsModal(cell.row.original.id)}
                                                >
                                                    <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            ) : (
                                                <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement="top"
                                                >
                                                    <div
                                                        className="ml-auto"
                                                        style={{
                                                            cursor: 'not-allowed',
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <Dropdown.Item
                                                            style={{pointerEvents: 'none'}}
                                                            disabled
                                                            data-cy="channel-evaluations-tasks-list-btn">
                                                            <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                        </Dropdown.Item>
                                                    </div>
                                                </Tooltip>
                                            )
                                        }
                                        {
                                            userCanDeleteChannelsEvaluationsTasksResource ? (
                                                <Dropdown.Item
                                                    onClick={() => openDeleteConfirm(cell.row.original.id, cell.row.original.name)}
                                                    data-cy="channel-evaluations-tasks-list-delete-btn"
                                                >
                                                    <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                </Dropdown.Item>
                                            ) : (
                                                <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement="top"
                                                >
                                                    <div
                                                        className="ml-auto"
                                                        style={{
                                                            cursor: 'not-allowed',
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <Dropdown.Item
                                                            style={{pointerEvents: 'none'}}
                                                            disabled
                                                            data-cy="channel-evaluations-tasks-list-delete-disabled-btn"
                                                        >
                                                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                        </Dropdown.Item>
                                                    </div>
                                                </Tooltip>
                                            )
                                        }
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Tooltip>
                        </div>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <Row className="mb-3">
                <Col lg={12} className="d-flex justify-content-end">
                    {userCanCreateChannelsEvaluationsTasksResource ? (
                        <Tooltip content={i18n.t('jt_add.text')} placement="top">
                            <Button
                                variant="outline-info"
                                size="sm"
                                icon="mdi-plus"
                                onClick={() => setSettingsModalOpen(true)}
                            >
                                {i18n.t('jt_add_new_evaluation_task.text')}
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                            placement="top"
                        >
                            <div style={{cursor: 'not-allowed', display: 'flex'}}>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    icon="mdi-plus"
                                    disabled
                                    style={{pointerEvents: 'none'}}
                                    data-cy="channel-evaluations-tasks-list-plus-outlinesecondary-btn" >
                                    {i18n.t('jt_add_new_evaluation_task.text')}
                                </Button>
                            </div>
                        </Tooltip>
                    )}
                </Col>
            </Row>

            <HelpTextBlock helpText={i18n.t('ht_you_can.text')} />

            {loading ? (
                <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
            ) : (
                <>
                    <div className="card">
                        <div className="card-body">
                            <TableComponent data={channelEvaluationsTasks} columns={columns} />
                        </div>
                    </div>

                    {settingsModalOpen && (
                        <ChannelEvaluationsTasksModal
                            isOpen={settingsModalOpen}
                            onClose={closeSettingsModal}
                            activeEvaluationId={selectedEvaluationTaskId}
                            activeChannelId={channelId || 0}
                        />
                    )}
                </>
            )}

            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={
                    itemToDelete
                        ? `${i18n.t('confirm_delete_channel_evaluation_task.text')}`
                        : ''
                }
                dismissible={!isDeleting}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting,
                    },
                ]}
            />
        </>
    );


});

export default ChannelEvaluationTasksListView; 